import type { AnalyzeResult } from '@/api/generated/types'
import type { ClientConfig } from '@/api/generated/types'
import { pushSpec, pushWizard } from '@/utils/account/accountPush'
import { StorageKeys, storageService } from '@/utils/localStorageService'

/**
 * The audience the run was drawn from, as the planner counted it at the moment of the run.
 *
 * Persisted WHOLE rather than as one headline number. It used to be `pool: number` — the year count
 * only — and nothing ever read it back, so returning to step 6 restored the rows and lost the
 * measurement: the audience block vanished, and the checkout priced against a pool of zero and
 * reported "unmeasured" reach on a search that had been measured perfectly well.
 *
 * These are the RUN's numbers, not today's. That is the honest version: the block describes the pool
 * this run selected from, and the run is from that moment too.
 */
export type PoolCounts = {
  /**
   * ТРИ ПОЛОСЫ, форма от 2026-09-11. Кольца ICP (ядро/соседи/поле) были собраны 2026-09-14 и в тот же
   * день сняты решением продукта — см. память `icp-rings-are-the-bands`; не собирать заново молча.
   *
   * Синяя и зелёная — ДВА ОКНА ОДНОГО СЧЁТА (полная спека клиента, 1000 и 30 дней), поэтому
   * зелёная ⊆ синей гарантированно, а не проверяется. Что этот счёт означает, решает полоса движка:
   *
   *   broad    синяя — подходят под фильтры клиента со старшинством; зелёная — и заходили за 30 дней.
   *   precise  синяя — попросили словами клиента (фильтры на этой полосе движок не применяет,
   *            `lanes.ts`); зелёная — попросили и заходили за 30 дней.
   *
   * СЕРАЯ — НЕ НАДМНОЖЕСТВО, А ДОБАВКА. Она хранится сразу как свой сегмент (`rest`), не как total,
   * из которого надо вычитать: на текстовой полосе структурный счёт с текстовым не вложены (замер
   * #527: по фильтрам 114, по словам 607), и вычитание там невозможно. Вместо этого серая считается
   * НЕПЕРЕСЕКАЮЩЕЙСЯ по построению:
   *
   *   broad    та же спека без старшинства минус синяя — одно семейство запросов, вложенность
   *            сверена по `structural.applied`;
   *   precise  фильтры клиента за 1000 дней с `exclude` написавших словами.
   *
   * Складывать можно только непересекающееся; `rest` — это уже оно. Герой и золотая кнопка — сумма
   * трёх сегментов, то есть объединение «подходят под фильтры» и «попросили словами».
   */

  /** Серая, УЖЕ КАК СЕГМЕНТ: сколько человек в базе сверх синей. `null` — не измерили (не рисуется). */
  rest: number | null
  /** Синяя, total: полная спека, окно 1000 дней. На текстовой полосе — все, кто просил словами. */
  core: number | null
  /** Зелёная, total: полная спека, окно 30 дней по `updated_at`. Про саму просьбу дата в индексе
      не хранится — `need_age_days` приходит из обогащения после выборки, — так что 30 дней здесь
      это активность в сети, не возраст запроса. */
  fresh: number | null
  /**
   * ВМЕСТИМОСТЬ ПРОГОНА: полная спека за 365 дней — ровно тот запрос, которым прогон тянет людей
   * (`fresh_days: 365`). Не полоса. Его читает лоадер, thank-you и запасной ход кнопки.
   */
  reach: number
  /**
   * ЧТО ИЗМЕРЯЕТ ЗЕЛЁНАЯ. 'recent' — структурный прогон: активность за 30 дней. 'asked' — текстовый:
   * у каждого в пуле глагол просьбы рядом с нужным словом, плюс активность за 30 дней. Подписи сейчас
   * одни на обе полосы движка (решение продукта), поле оставлено, чтобы честные подписи возвращались
   * одной строкой.
   */
  signalMeans: 'recent' | 'asked' | null
  /** Применил ли движок старшинство. Без него синяя равна серой и не рисуется. */
  seniorityApplied: boolean
  /** Считались ли полосы вообще: только `broad` и `precise` со словами. На `pain`/`beacon` — нет. */
  banded: boolean
}

/** What the wizard keeps about its own progress, separate from the spec it is editing. */
export type WizardState = {
  step: number
  done: Record<number, boolean>
  runId: number | null
  pool: PoolCounts | null
  counts: Record<string, number> | null
  /** Подписанный доступ к своему прогону (SearchResult.token): открывает три контакта и план. */
  token: string | null
  /**
   * КЛЮЧ ПОДБОРА НАЧАТОГО ПОИСКА. Чеканится браузером ПЕРЕД выборкой и хранится здесь именно потому,
   * что `runId` к этому моменту ещё не существует: прогон, ответ о котором не доехал (замер
   * 2026-09-22 — свой таймаут браузера на 180 с), опознаётся только им.
   *
   * Живёт ровно до ответа: пришёл прогон — ключ гасится. Пока он стоит, где-то на сервере может
   * лежать оплаченный квотой прогон, за которым можно вернуться бесплатно (LaunchStore.recoverRun).
   */
  resumeKey: string | null
  /**
   * Право ОЦЕНИТЬ прогон без права видеть контакты — токен ссылки «вернитесь к шортлисту» из письма
   * (resumeFromLink.ts). С 2026-09-23 оценка — только владельцу, и прогон, открытый по ссылке на другом
   * устройстве, без этого поля оставался бы неоценённым.
   */
  scoreToken?: string | null
}

/* Written as a bare number by every build before the audience block existed: a stored number arrives
   here as a PoolCounts whose every field is undefined and renders as NaN. Дальше — та же защита от
   предыдущей ФОРМЫ: до этой сборки в полях лежали окна активности под подписями про намерение, и
   прочитать их как просьбу значило бы восстановить ровно ту ложь, ради которой форма и менялась.
   Старая запись отбрасывается целиком, прогон посчитает заново. */
const normalisePool = (value: unknown): PoolCounts | null => {
  if (!value || typeof value !== 'object') return null
  const pool = value as Partial<PoolCounts> & { year?: unknown; signalled?: unknown; base?: unknown; fieldNarrows?: unknown }
  /* СТАРЫЕ ФОРМЫ ПЕРЕВОДЯТСЯ, А НЕ ВЫБРАСЫВАЮТСЯ: `restore()` пул не пересчитывает, и запись, которую
     мы не поняли, оставляет кнопку без числа. Боевая сборка писала `year` — это то же окно, что
     `reach`. Форма с `signalled` (окно 90 дней) в новую не переводится: полосы у неё другие, они
     остаются `null` и не рисуются. Форма колец (`fieldNarrows`, один день 2026-09-14) — тоже. */
  const reach = typeof pool.reach === 'number' ? pool.reach : typeof pool.year === 'number' ? pool.year : null
  if (reach === null) return null
  const num = (v: unknown) => (typeof v === 'number' ? v : null)
  if ('fieldNarrows' in pool) return { rest: null, core: null, fresh: null, reach, signalMeans: null, seniorityApplied: false, banded: false }
  const core = num(pool.core)
  /* Форма одного дня (2026-09-11) писала серую как total `base` ⊇ `core`; сегмент — их разность. */
  const base = num(pool.base)
  const rest = num(pool.rest) ?? (base !== null && core !== null ? Math.max(0, base - core) : null)
  return {
    rest,
    core,
    fresh: num(pool.fresh),
    reach,
    signalMeans: pool.signalMeans === 'recent' || pool.signalMeans === 'asked' ? pool.signalMeans : null,
    seniorityApplied: pool.seniorityApplied === true,
    banded: pool.banded === true,
  }
}

type StoredSpec = { at: string; spec: Partial<ClientConfig> }

export const loadSpec = (): Partial<ClientConfig> =>
  storageService.getItem<StoredSpec>(StorageKeys.plgSpec)?.spec ?? {}

/**
 * The nouns are the one field that never persists. They are only valid for the client they were
 * measured against, and a stale list is worse than an empty one because the empty case at least
 * fails visibly — two runs went out on the previous client's audience through exactly this door.
 */
export const saveSpec = (spec: Partial<ClientConfig>) => {
  const { objects: _objects, objectsFor: _objectsFor, ...rest } = spec
  const at = new Date().toISOString()
  storageService.setItem(StorageKeys.plgSpec, { at, spec: rest })
  /* Контакт на сервер со спекой не едет: почта, имя и роль живут в аккаунте. */
  const { contactEmail: _email, contactName: _name, contactRole: _role, ...withoutContact } = rest
  pushSpec({ at, value: withoutContact })
}

export const loadWizardState = (): WizardState => {
  const stored = storageService.getItem<Partial<WizardState>>(StorageKeys.plgApp) ?? {}
  return {
    step: 1,
    done: {},
    runId: null,
    counts: null,
    token: null,
    resumeKey: null,
    ...stored,
    pool: normalisePool(stored.pool),
  }
}

export const saveWizardState = (state: WizardState) => {
  const at = new Date().toISOString()
  storageService.setItem(StorageKeys.plgApp, state)
  storageService.setItem(StorageKeys.plgAppAt, at)
  pushWizard({ at, value: state })
}

/**
 * Fold a finished product read into the spec. Two things are deliberately NOT carried:
 *   - the search nouns (see saveSpec);
 *   - `sellsSrc`/`buyerSrc` are marked 'auto', never 'operator'. The app treats operator text as
 *     untouchable and auto text as replaceable, and calling a machine read "typed" freezes a guess.
 */
export const applyReadToSpec = (
  spec: Partial<ClientConfig>,
  read: AnalyzeResult,
  url: string,
): Partial<ClientConfig> => ({
  ...spec,
  url,
  sells: read.sells,
  /* Ценностное предложение из чтения (2026-10-08). На карточке лида НЕ печатается: блок «Value proposition» Дима
     снял в тот же день (одна строка на всех карточках), поле оставлено как факт чтения. */
  valueProp: read.value_prop ?? '',
  buyer: read.buyer,
  sellsSrc: 'auto',
  buyerSrc: 'auto',
  targetside: read.target_side || 'buyer',
  lane: read.lane || 'precise',
  sideSource: 'auto',
  laneSource: 'auto',
  sideWhy: read.target_side_why ?? '',
  laneWhy: read.lane_why ?? '',
  /* Делает ли покупатель сам работу продукта (2026-10-05): факт для оценки, рядом с targetside. */
  buyerRole: read.buyer_role ?? 'unclear',
  buyerRoleWhy: read.buyer_role_why ?? '',
  /* Покупатель — человек в переходе (2026-10-06): слайд о поиске работы тогда не прячет строку. */
  buyerInTransition: read.buyer_in_transition ?? false,
  buyerInTransitionWhy: read.buyer_in_transition_why ?? '',
  /* Страны рынка со страницы (2026-10-06): ярус страны подставляет withMarketCountries, не это место —
     здесь только факт, чтобы сброс фильтров при смене продукта (forgetPreviousProduct) его не потерял. */
  marketCountries: read.market_countries ?? [],
  marketCountriesWhy: read.market_countries_why ?? '',
  readConf: read.confidence ?? '',
  buyerSegments: read.buyer_segments ?? null,
  channelFit: read.channel_fit ?? '',
  channelFitWhy: read.channel_fit_why ?? '',
  readWhat: read.read_what ?? '',
  weakestPoint: read.weakest_point ?? '',
  pageChars: read.page?.chars ?? 0,
  pagePages: read.page?.pages ?? null,
  analyzedFor: `${(read.sells ?? '').trim()} § ${url.trim()}`,
})

/**
 * Everything client-specific the pre-fill does NOT propose is RESET, not left alone: whatever the
 * last product left in the spec survives a switch silently. Measured — one client inherited another's
 * must-have keywords and lost 87% of its pool to a filter about someone else's market (704 → 92).
 */
export const resetClientSpecificFilters = (spec: Partial<ClientConfig>): Partial<ClientConfig> => ({
  ...spec,
  indEx: [],
  kwin: '',
  exkw: '',
  titleveto: [],
  cotype: [],
  comp: '',
  proof: '',
  give: '',
  segs: [],
  /* География сбрасывается вместе с продуктом и подставляется заново со страницы НОВОГО продукта
     (withMarketCountries). До 2026-09-29 страна была фильтром, и чужая страна молча ампутировала рынок —
     поэтому её никогда не предлагали; теперь это ярус (своя страна сверху, остальные ниже), и ошибка
     стоит порядка, а не людей. */
  geo: {},
  geoPerson: false,
})

/**
 * ЯРУС СТРАНЫ СО СТРАНИЦЫ (2026-10-06, шаг 4 плана по выдаче). Подставляется только когда страна ещё не
 * выбрана: выбор человека сильнее чтения. Бенчмарк 888: страховка «nationwide» для США искалась по
 * миру, в первой десятке — Франция, Мальта и ЮАР. Снять — одним движением в визарде, как любой чип.
 */
export const withMarketCountries = (spec: Partial<ClientConfig>): Partial<ClientConfig> => {
  const chosen = Object.keys(spec.geo ?? {}).length > 0
  const proposed = (spec.marketCountries ?? []).map(String).filter(Boolean)
  if (chosen || !proposed.length) return spec
  return { ...spec, geo: Object.fromEntries(proposed.map((name) => [name, 1])), geoPerson: true }
}

/**
 * СМЕНИЛСЯ ПРОДУКТ — ЗАБЫВАЕМ ВСЕ ФИЛЬТРЫ ПРОШЛОГО, а не только те, что модель не предлагает.
 *
 * `resetClientSpecificFilters` выше намеренно НЕ трогает пять списков, которые модель подбирает
 * сама (должности, функции, старшинство, индустрии, размеры): он зовётся из `fillTargeting` уже
 * поверх свежего предложения, и обнулить их там значило бы стереть только что подобранное.
 *
 * Но при смене продукта эти пять — самое опасное, что остаётся: заполнять модель будет только
 * ПУСТЫЕ поля (`keep` в targeting.ts), поэтому непустые индустрии прошлого продукта дожили бы до
 * нового прогона молча. Замерено 2026-09-21 на стенде: спека rask.ai (шесть индустрий про подкасты
 * и контент, шесть должностей) пережила чтение intch.org и показалась человеку как его собственный
 * ответ на первом же вопросе воронки 2. Цена такого наследования уже известна по другому случаю:
 * чужие обязательные ключевые слова срезали пул клиента с 704 до 92.
 *
 * Зовётся ровно в одном месте — на подтверждении продукта, когда штамп `analyzedFor` изменился
 * (containers/Onboard/onboardSpec.ts). До первого вопроса терять нечего, после — уже нельзя.
 */
export const forgetPreviousProduct = (spec: Partial<ClientConfig>): Partial<ClientConfig> => ({
  ...resetClientSpecificFilters(spec),
  titles: [],
  func: [],
  sen: [],
  indIn: [],
  sizes: [],
  acct: [],
})

/**
 * ОНБОРДИНГ ОТВЕТИЛ ЗА ВСЕ ПЯТЬ ШАГОВ, и воронка ведёт прямо на шестой — решение продукта от
 * 2026-09-15 («давай упростим воронку… сразу вести на ага-момент»): между письмом и выдачей не
 * остаётся ни одного экрана. Шаги 2–5 при этом никуда не деваются: рельс их показывает и пускает
 * назад, а фильтры правятся листом «Adjust» на самой выдаче.
 *
 * ЧТО ЗДЕСЬ БЫЛО И ПОЧЕМУ ЭТО ВАЖНО ЗНАТЬ. До этого дня функция отмечала РОВНО ОДИН шаг (продукт) и
 * открывала визард на втором, и у этого была измеренная причина: отметка 2–5 «на основании
 * заполненных полей» ставила пять галочек и 83% прогресса на шагах, по которым человек не ходил, а
 * слияние карт оставляло эти галочки стоять — рельс утверждал, что индустрии и размер компании
 * отвечены, пока человек был на ролях. Новый случай отличается не механикой, а СМЫСЛОМ ГАЛОЧКИ:
 * тогда поля просто оказывались непустыми, а шаг всё равно ждал человека; теперь продукт отвечает за
 * него сознательно и этих шагов ему не показывает. Галочка говорит «на этот вопрос ответ есть», и
 * это правда.
 *
 * Карта по-прежнему ПЕРЕЗАПИСЫВАЕТСЯ, а не сливается: новый проход онбординга не должен наследовать
 * галочки прошлого.
 */
export const markOnboardingAnswered = (upTo = 5, { clearRun = false }: { clearRun?: boolean } = {}) => {
  const state = loadWizardState()
  /* `upTo` — сколько шагов визарда закрыл онбординг к этому моменту (ANSWERED_ON_CONFIRM /
     ANSWERED_ON_HANDOFF в utils/spec/funnelPath.ts, там же — почему это ДВА момента). Отмеченные
     шаги — те, за которые продукт отвечает сознательно и которых человеку не показывает. */
  const done: Record<number, boolean> = {}
  for (let step = 1; step <= upTo; step += 1) done[step] = true
  /**
   * `clearRun` — УКАЗАТЕЛИ НА ПРОШЛЫЙ ПРОГОН ОБНУЛЯЮТСЯ ПРИ СМЕНЕ ПРОДУКТА. Иначе человек, который в
   * том же браузере прочитал другой сайт, попадал после контакта не на свою новую выдачу, а на
   * восстановленный чужой прогон: `runId`, `pool` и `token` переживали смену через `...state`, а
   * проверка устаревшести на выдаче не срабатывает (savedSignature приходит пустым). Прогон этим не
   * теряется — он остаётся в истории поисков, — теряется только указатель «покажи вот этот».
   */
  const pointers = clearRun ? { runId: null, pool: null, counts: null, token: null } : {}
  saveWizardState({ ...state, ...pointers, done, step: upTo + 1 })
}
